// HistorialComportamientoReportesOperadores.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  CircularProgress,
  Button,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import GeneralContext from "../GeneralContext/GeneralContext";

import PageBody from "../GeneralComponents/PageBody";
import InputFilter from "../GeneralComponents/InputFilter";

import axios from "../../api/axios";

// =====================
// UI constants/helpers
// =====================
const MONTHS = [
  { key: "01", label: "Enero" },
  { key: "02", label: "Febrero" },
  { key: "03", label: "Marzo" },
  { key: "04", label: "Abril" },
  { key: "05", label: "Mayo" },
  { key: "06", label: "Junio" },
  { key: "07", label: "Julio" },
  { key: "08", label: "Agosto" },
  { key: "09", label: "Septiembre" },
  { key: "10", label: "Octubre" },
  { key: "11", label: "Noviembre" },
  { key: "12", label: "Diciembre" },
];

// Anchos FIJOS
const OP_COL_W = 320;
const MONTH_COL_W = 140;

const monthKey = (d) => String(d).slice(0, 7); // "YYYY-MM"
const yearOf = (d) => Number(String(d).slice(0, 4));
const monthsOfYear = (year) => MONTHS.map((m) => `${year}-${m.key}`);

const CURRENT_YEAR = new Date().getFullYear();
function buildYearRange(from = 2025, to = CURRENT_YEAR) {
  const out = [];
  for (let y = from; y <= to; y++) out.push(y);
  return out;
}

function dotColor(present, total) {
  if (!total) return "#bdbdbd";
  if (present === 0) return "#e53935";
  if (present < total) return "#fb8c00";
  return "#43a047";
}

const yearBtnSx = (active) => ({
  width: "100%",
  height: 44,
  borderRadius: 0.75,
  textTransform: "none",
  fontWeight: 700,
  justifyContent: "center",
  border: "1px solid rgba(0,0,0,0.15)",
  ...(active
    ? {
        bgcolor: "#AFC7DF",
        color: "#1f2733",
        "&:hover": { bgcolor: "#A3BED8" },
      }
    : {
        bgcolor: "#fff",
        color: "#1f2733",
        "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
      }),
});


// =====================
// Data helpers
// =====================
function buildPresenceIndex(apiData) {
  const byFuente = new Map();

  (apiData?.fuentes ?? []).forEach((f) => {
    const byOper = new Map();
    (f.operadores ?? []).forEach((op) => {
      const set = new Set((op.periodos_presentes ?? []).map(monthKey));
      byOper.set(op.nombre_operador, set);
    });
    byFuente.set(f.fuente, byOper);
  });

  return byFuente;
}

function extractYears(apiData) {
  const years = new Set();
  (apiData?.fuentes ?? []).forEach((f) => {
    (f.operadores ?? []).forEach((op) => {
      (op.periodos_presentes ?? []).forEach((p) => years.add(yearOf(p)));
      (op.periodos_faltantes ?? []).forEach((p) => years.add(yearOf(p)));
    });
  });
  return Array.from(years).filter(Boolean).sort((a, b) => b - a);
}

function buildGrid({ apiData, selectedYear, selectedFuente = null }) {
  const idx = buildPresenceIndex(apiData);

  const fuentes =
    selectedFuente === null ? Array.from(idx.keys()) : [selectedFuente].filter((x) => idx.has(x));

  const opsSet = new Set();
  fuentes.forEach((fu) => {
    const mapOp = idx.get(fu);
    if (mapOp) Array.from(mapOp.keys()).forEach((name) => opsSet.add(name));
  });
  const operadores = Array.from(opsSet).sort((a, b) => a.localeCompare(b));

  const months = monthsOfYear(selectedYear);
  const total = fuentes.length;

  const rows = operadores.map((opName) => {
    const cells = months.map((ym) => {
      let present = 0;
      fuentes.forEach((fu) => {
        const set = idx.get(fu)?.get(opName);
        if (set?.has(ym)) present += 1;
      });
      return { ym, present, total };
    });

    return { operador: opName, cells };
  });

  return { idx, fuentes, months, rows };
}

export default function HistorialReportes() {
  const { pageTitle, setPageTitle } = useContext(GeneralContext);

  const [loading, setLoading] = useState(false);
  const [apiData, setApiData] = useState(null);

  const [selectedFuente, setSelectedFuente] = useState(null);
  const [selectedOperador, setSelectedOperador] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  const [expandedOps, setExpandedOps] = useState(() => new Set());

  const yearButtons = useMemo(() => {
    const ys = buildYearRange(2025, CURRENT_YEAR);
    return ys.sort((a, b) => b - a);
  }, []);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        const resp = await axios.getHistorialReportes?.();
        const data = resp?.data ?? resp;

        if (!alive) return;

        setApiData(data);

        // toma el más reciente disponible, si no fallback al current
        const yearsFromData = extractYears(data);
        const y0 = yearsFromData?.find((y) => y >= 2025 && y <= CURRENT_YEAR) ?? CURRENT_YEAR;

        setSelectedYear(y0);
        setSelectedFuente(null);
        setSelectedOperador(null);
        setExpandedOps(new Set());
      } catch (e) {
        if (!alive) return;
        console.error("Error cargando historial:", e);
        setApiData(null);
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, []);

  const reportOptions = useMemo(() => {
    const f = (apiData?.fuentes ?? []).map((x) => x.fuente);
    return [...Array.from(new Set(f)).sort((a, b) => a.localeCompare(b))];
  }, [apiData]);

  const grid = useMemo(() => {
    if (!apiData || !selectedYear) return { idx: new Map(), fuentes: [], months: [], rows: [] };
    return buildGrid({ apiData, selectedYear, selectedFuente });
  }, [apiData, selectedYear, selectedFuente]);

  const operatorOptions = useMemo(() => grid.rows.map((r) => r.operador), [grid.rows]);

  const visibleRows = useMemo(() => {
    if (!selectedOperador) return grid.rows;
    return grid.rows.filter((r) => r.operador === selectedOperador);
  }, [grid.rows, selectedOperador]);

  const toggleExpand = (opName) => {
    setExpandedOps((prev) => {
      const next = new Set(prev);
      if (next.has(opName)) next.delete(opName);
      else next.add(opName);
      return next;
    });
  };

  const collapseAllOps = () => setExpandedOps(new Set());
  const expandAllOps = () => setExpandedOps(new Set(visibleRows.map((r) => r.operador)));

  const isAllExpanded = useMemo(() => {
    if (!visibleRows.length) return false;
    return visibleRows.every((r) => expandedOps.has(r.operador));
  }, [visibleRows, expandedOps]);

  const handleYearSelect = (y) => {
    setSelectedYear(y);
    setExpandedOps(new Set());
  };

  const handleFilter = (item, value) => {
    if (item === "reporte") {
      setSelectedFuente(value ?? null);
      setExpandedOps(new Set());
      return;
    }
    if (item === "operador") {
      setSelectedOperador(value ?? null);
      setExpandedOps(new Set());
      return;
    }
  };

  const detailRowsForOperator = (opName) => {
    if (selectedFuente !== null) return [];

    const fuentes = Array.from(grid.idx.keys()).sort((a, b) => a.localeCompare(b));
    const months = grid.months;

    return fuentes.map((fuente) => {
      const set = grid.idx.get(fuente)?.get(opName) ?? new Set();
      const cells = months.map((ym) => ({
        ym,
        present: set.has(ym) ? 1 : 0,
        total: 1,
      }));
      return { fuente, cells };
    });
  };

  useEffect(() => {
    setPageTitle("Listado histórico de pre-facturas");
  }, [pageTitle, setPageTitle]);

  const tableFixedSx = {
    tableLayout: "fixed",
    width: OP_COL_W + MONTH_COL_W * 12,
    "& th, & td": { boxSizing: "border-box", mx: 0 },
  };

  const monthCellSx = { width: MONTH_COL_W, minWidth: MONTH_COL_W };

  const stickyFirstColBodySx = {
    width: OP_COL_W,
    minWidth: OP_COL_W,
    position: "sticky",
    left: 0,
    zIndex: 3, 

    bgcolor: "background.paper",
  };

    const stickyHeadSx = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  bgcolor: "background.paper",
};

const stickyFirstColHeadSx = {
  width: OP_COL_W,
  minWidth: OP_COL_W,
  position: "sticky",
  left: 0,
  top: 0,  
  zIndex: 10,
  bgcolor: "background.paper",
};



  const yearColSize = useMemo(() => {
    const n = yearButtons.length || 1;
    return Math.max(2, Math.floor(12 / n));
  }, [yearButtons.length]);


  const expandedRowSx = {
    backgroundColor: "#B0C9E2!important",
  };

  return (
    <PageBody>
      <Box sx={{ p: 2 }}>
          <Stack spacing={1} alignItems="center" sx={{mb: 3}}>
            <Typography
            variant="h1"
            color="inherit"
            noWrap
            sx={{ textAlign: "center", padding: "0.5rem" }}
          >
              ESTADO DE RECEPCIÓN DE REPORTES
            </Typography>

            <Grid container spacing={1} alignItems="center" sx={{ mt: 0.5, width: "100%" }}>
              <Grid item size={{ xs: 12, md: 6 }}>
                <InputFilter
                  item="reporte"
                  name="Reporte"
                  data={[]}
                  opt={reportOptions}
                  defaultOpt={[]}
                  value={selectedFuente}
                  callback={handleFilter}
                  disabled={!apiData || loading}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <InputFilter
                  item="operador"
                  name="Operador"
                  data={[]}
                  opt={operatorOptions}
                  defaultOpt={[]}
                  value={selectedOperador}
                  callback={handleFilter}
                  disabled={!apiData || loading}
                />
              </Grid>
            </Grid>

            <Grid container spacing={1} alignItems="center" sx={{ mt: 0.5, width: "100%" }}>
              {yearButtons.map((y) => {
                const active = selectedYear === y;
                const showIcon = active;

                return (
                  <Grid key={y} item size={{ xs: 12, sm: yearColSize, md: yearColSize }}>
                    <Button
                      fullWidth
                      variant="contained"
                      disableElevation
                      disabled={!apiData || loading}
                      onClick={() => handleYearSelect(y)}
                      sx={yearBtnSx(active)}
                    >
                      <Box sx={{ position: "relative", width: "100%" }}>
                        <Typography
                          sx={{
                            position: "absolute",
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            fontWeight: 700,
                          }}
                        >
                          {y}
                        </Typography>
                        {showIcon ? (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation(); // no cambia el año
                              if (isAllExpanded) collapseAllOps();
                              else expandAllOps();
                            }}
                            sx={{
                              position: "absolute",
                              right: 6,
                              top: "50%",
                              transform: "translateY(-50%)",
                              p: 0.25,
                            }}
                          >
                            {isAllExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                          </IconButton>
                        ) : null}
                      </Box>
                    </Button>

                  </Grid>
                );
              })}
            </Grid>

          </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !apiData ? (
          <Paper sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
            Sin datos
          </Paper>
        ) : (

        <>
<Box
  variant="outlined"
  sx={{
    px: 2,
    py: 1,
    bgcolor: "#AFC7DF",
    borderColor: "rgba(0,0,0,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  }}
>
  <Typography sx={{ fontWeight: 800 }}>
    Conteo consolidado de Reportes {selectedYear}
  </Typography>

  <IconButton
    size="small"
    onClick={() => {
      if (selectedFuente !== null) return;
      if (isAllExpanded) collapseAllOps();
      else expandAllOps();
    }}
    disabled={selectedFuente !== null || loading || !apiData}
    
  >
    {isAllExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
  </IconButton>
</Box>


          <TableContainer
            component={Paper}
              variant="outlined"
              sx={{ maxHeight: "70vh" }} 
            >



             <Table size="small" stickyHeader sx={tableFixedSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...stickyFirstColHeadSx, fontWeight: 700 }}>
                    Operador
                  </TableCell>

                  {MONTHS.map((m) => (
                    <TableCell
                      key={m.key}
                      align="center"
                      sx={{ ...monthCellSx, ...stickyHeadSx, fontWeight: 700 }}
                    >
                      {m.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleRows.map((r) => {
                  const allowExpand = selectedFuente === null;
                  const isExpanded = expandedOps.has(r.operador);
                  const detailRows = allowExpand ? detailRowsForOperator(r.operador) : [];

                  return (
                    <React.Fragment key={r.operador}>
                      <TableRow  sx={isExpanded ? expandedRowSx : "background.paper"}>
                        <TableCell sx={{ ...stickyFirstColBodySx, fontWeight: 700, bgcolor: isExpanded ? expandedRowSx : "background.paper"  }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {allowExpand ? (
                              <IconButton size="small" onClick={() => toggleExpand(r.operador)}>
                                {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                              </IconButton>
                            ) : (
                              <Box sx={{ width: 34 }} />
                            )}
                            <span>{r.operador}</span>
                          </Stack>
                        </TableCell>

                        {r.cells.map((c) => (
                          <TableCell key={c.ym} align="center" sx={monthCellSx}>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "999px",
                                  bgcolor: dotColor(c.present, c.total),
                                }}
                              />
                              <Typography component="span" sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                                {c.present}/{c.total}
                              </Typography>
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>

                      {allowExpand ? (
                        <TableRow>
                          <TableCell colSpan={13} sx={{ p: 0, border: 0 }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Table size="small" sx={tableFixedSx}>
                                <TableBody>
                                  {detailRows.map((d) => (
                                    <TableRow key={d.fuente} hover>
                                      <TableCell sx={{ ...stickyFirstColBodySx, px: 4 }}>
                                        <Typography sx={{ fontSize: "0.86rem", fontWeight: 700 }}>
                                          {d.fuente}
                                        </Typography>
                                      </TableCell>

                                      {d.cells.map((c) => (
                                        <TableCell key={c.ym} align="center" sx={monthCellSx}>
                                          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                                            <Box
                                              sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: "999px",
                                                bgcolor: dotColor(c.present, c.total),
                                              }}
                                            />
                                            <Typography component="span" sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                                              {c.present === 0 ? "-" : c.ym}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          </>
        )}
      </Box>
    </PageBody>
  );
}
